"use client";

import React from "react";
import { Card, CardBody, CardHeader, Spinner } from "@heroui/react";

import { logger } from "@/telemetry";

export interface CSVViewerProps {
  /** CSV/TSV document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Citation text to display (shown for reference, no highlighting) */
  citationText?: string;

  /** Filename to determine delimiter (CSV vs TSV) */
  filename?: string;
}

/**
 * CSV/TSV Viewer component
 * Displays tabular data in a scrollable table
 *
 * NOTE: Like images, citations are shown for reference but not highlighted in the table.
 * Users can visually locate the data in the table.
 */
export const CSVViewer: React.FC<CSVViewerProps> = ({ documentData, citationText, filename }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tableData, setTableData] = React.useState<string[][]>([]);
  const [headers, setHeaders] = React.useState<string[]>([]);

  // Parse CSV/TSV data
  React.useEffect(() => {
    if (!documentData) {
      return;
    }

    let isMounted = true;

    const parseCSV = async () => {
      try {
        setLoading(true);
        setError(null);

        // Decode ArrayBuffer as UTF-8 text
        const decoder = new TextDecoder("utf-8");
        const text = decoder.decode(documentData);

        // Determine delimiter (comma for CSV, tab for TSV)
        const isTSV = filename?.toLowerCase().endsWith(".tsv");
        const delimiter = isTSV ? "\t" : ",";

        // Parse CSV/TSV into rows
        const rows = parseCSVText(text, delimiter);

        if (!isMounted) return;

        if (rows.length === 0) {
          setError("Empty file");
          setLoading(false);

          return;
        }

        // First row as headers
        setHeaders(rows[0]);

        // Remaining rows as data (limit to first 1000 rows for performance)
        const dataRows = rows.slice(1, 1001);

        setTableData(dataRows);
        setLoading(false);
      } catch (err) {
        logger.error("❌ Failed to parse CSV/TSV", err);
        if (isMounted) {
          setError("Failed to parse CSV/TSV file");
          setLoading(false);
        }
      }
    };

    parseCSV();

    return () => {
      isMounted = false;
    };
  }, [documentData, filename]);

  /**
   * Simple CSV parser
   * Handles basic CSV with quoted fields
   */
  const parseCSVText = (text: string, delimiter: string): string[][] => {
    const rows: string[][] = [];
    const lines = text.split(/\r?\n/);

    for (const line of lines) {
      if (line.trim().length === 0) continue;

      const row: string[] = [];
      let currentField = "";
      let insideQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          // Toggle quote state
          if (insideQuotes && line[i + 1] === '"') {
            // Escaped quote
            currentField += '"';
            i++; // Skip next quote
          } else {
            insideQuotes = !insideQuotes;
          }
        } else if (char === delimiter && !insideQuotes) {
          // Field separator
          row.push(currentField.trim());
          currentField = "";
        } else {
          currentField += char;
        }
      }

      // Add last field
      row.push(currentField.trim());
      rows.push(row);
    }

    return rows;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner label="Loading CSV/TSV..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-danger-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      <div className="max-w-full space-y-4">
        {/* Table Display */}
        <Card>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-default-100 sticky top-0 z-10">
                  <tr>
                    {/* Row number column */}
                    <th className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200">
                      #
                    </th>
                    {headers.map((header, idx) => (
                      <th
                        key={idx}
                        className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200 whitespace-nowrap"
                      >
                        {header || `Column ${idx + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIdx) => (
                    <tr key={rowIdx} className="border-b border-default-200 hover:bg-default-50">
                      {/* Row number */}
                      <td className="px-3 py-2 text-xs text-default-500 border-r border-default-200 bg-default-50">
                        {rowIdx + 1}
                      </td>
                      {row.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="px-3 py-2 text-xs text-default-700 border-r border-default-200 whitespace-nowrap"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        {/* Show info if table is truncated */}
        {tableData.length === 1000 && (
          <div className="text-xs text-default-500 text-center">
            Showing first 1,000 rows (file may contain more)
          </div>
        )}

        {/* Citation Text Reference (if provided) */}
        {citationText && (
          <Card>
            <CardHeader className="pb-2">
              <h3 className="text-sm font-semibold text-default-700">
                Citation Text (locate visually in table above):
              </h3>
            </CardHeader>
            <CardBody className="pt-2">
              <p className="text-sm text-default-600 italic whitespace-pre-wrap">
                "{citationText}"
              </p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CSVViewer;
